import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InterestOptions, PageSection } from '@/types'

import Link from 'next/link'
import { useId, useState } from 'react'
import { ArrowUpRight, CheckCircle2, ChevronDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'

interface ContactFormProps {
    interests: InterestOptions
    defaultInterest?: string
    section?: PageSection | null
}

type Fields = {
    name: string
    email: string
    phone: string
    company: string
    interest: string
    message: string
    website: string
}

type Errors = Partial<Record<keyof Fields, string>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const validate = (fields: Fields): Errors => {
    const errors: Errors = {}

    if (fields.name.trim().length < 2) errors.name = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(fields.email.trim())) errors.email = 'Please enter a valid email address.'
    if (fields.message.trim().length < 10) errors.message = 'Please tell us a little more (at least 10 characters).'

    return errors
}

const inputClass = 'w-full border border-input bg-background px-4 py-3.5 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground/70 hover:border-foreground/40 focus:border-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 aria-[invalid=true]:border-destructive'

const ContactForm: FC<ContactFormProps> = ({ interests, defaultInterest = '', section }): ReactNode => {
    const id = useId()

    const emptyFields: Fields = {
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: defaultInterest,
        message: '',
        website: ''
    }

    const [fields, setFields] = useState<Fields>(emptyFields)
    const [errors, setErrors] = useState<Errors>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
    const [serverMessage, setServerMessage] = useState('')

    const fieldId = (name: keyof Fields) => `${id}-${name}`

    const update = (name: keyof Fields) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = event.target.value

        setFields(current => ({ ...current, [name]: value }))

        if (errors[name]) {
            setErrors(current => ({ ...current, [name]: undefined }))
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate(fields)
        setErrors(found)

        const firstInvalid = (Object.keys(found) as Array<keyof Fields>)[0]

        if (firstInvalid) {
            document.getElementById(fieldId(firstInvalid))?.focus()
            return
        }

        setStatus('sending')
        setServerMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fields)
            })

            const data = (await response.json().catch(() => null)) as { message?: string, errors?: Errors } | null

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)
                setServerMessage(data?.message ?? 'Something went wrong. Please try again in a moment.')
                setStatus('error')
                return
            }

            setStatus('sent')
        }
        catch {
            setServerMessage('We could not reach the server. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const reset = () => {
        setFields(emptyFields)
        setErrors({})
        setServerMessage('')
        setStatus('idle')
    }

    if (status === 'sent') {
        return (
            <div role='status' className='flex min-h-96 flex-col items-start justify-center'>
                <CheckCircle2 className='size-10 text-ocean' aria-hidden='true' />
                <h3 className='display mt-6 text-2xl'>Thank you</h3>
                <p className='mt-4 max-w-md leading-relaxed text-muted-foreground'>
                    Your inquiry has been received. Our team will review it and get back to you shortly.
                </p>
                <button
                    type='button'
                    onClick={reset}
                    className='mt-8 border border-foreground/25 px-6 py-3 transition-colors hover:border-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                >
                    <span className='eyebrow'>Send another inquiry</span>
                </button>
            </div>
        )
    }

    const errorFor = (name: keyof Fields) => errors[name]
        ? (
            <p id={`${fieldId(name)}-error`} className='mt-2 text-sm text-destructive'>
                {errors[name]}
            </p>
        )
        : null

    const labelClass = 'eyebrow mb-2.5 block text-foreground/70'

    return (
        <form noValidate onSubmit={onSubmit} aria-describedby={serverMessage ? `${id}-server` : undefined}>
            <div className='grid gap-6 sm:grid-cols-2'>
                <div>
                    <label htmlFor={fieldId('name')} className={labelClass}>Name *</label>
                    <input
                        id={fieldId('name')}
                        name='name'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={120}
                        value={fields.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${fieldId('name')}-error` : undefined}
                        className={inputClass}
                    />
                    {errorFor('name')}
                </div>

                <div>
                    <label htmlFor={fieldId('email')} className={labelClass}>Email *</label>
                    <input
                        id={fieldId('email')}
                        name='email'
                        type='email'
                        autoComplete='email'
                        required
                        maxLength={200}
                        value={fields.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${fieldId('email')}-error` : undefined}
                        className={inputClass}
                    />
                    {errorFor('email')}
                </div>

                <div>
                    <label htmlFor={fieldId('phone')} className={labelClass}>Phone</label>
                    <input
                        id={fieldId('phone')}
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        maxLength={40}
                        value={fields.phone}
                        onChange={update('phone')}
                        className={inputClass}
                    />
                </div>

                <div>
                    <label htmlFor={fieldId('company')} className={labelClass}>Company</label>
                    <input
                        id={fieldId('company')}
                        name='company'
                        type='text'
                        autoComplete='organization'
                        maxLength={160}
                        value={fields.company}
                        onChange={update('company')}
                        className={inputClass}
                    />
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor={fieldId('interest')} className={labelClass}>I am interested in</label>
                    <div className='relative'>
                        <select
                            id={fieldId('interest')}
                            name='interest'
                            value={fields.interest}
                            onChange={update('interest')}
                            className={cn(inputClass, 'appearance-none pr-12')}
                        >
                            <option value=''>Choose a topic</option>

                            {interests.services.length > 0 && (
                                <optgroup label='Services'>
                                    {interests.services.map(option => (
                                        <option key={`service-${option}`} value={option}>{option}</option>
                                    ))}
                                </optgroup>
                            )}

                            {interests.brands.length > 0 && (
                                <optgroup label='Brands'>
                                    {interests.brands.map(option => (
                                        <option key={`brand-${option}`} value={option}>{option}</option>
                                    ))}
                                </optgroup>
                            )}

                            <option value='Something else'>Something else</option>
                        </select>

                        <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-foreground/60' aria-hidden='true' />
                    </div>
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor={fieldId('message')} className={labelClass}>Message *</label>
                    <textarea
                        id={fieldId('message')}
                        name='message'
                        required
                        rows={6}
                        maxLength={5000}
                        value={fields.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? `${fieldId('message')}-error` : undefined}
                        className={cn(inputClass, 'resize-y')}
                    />
                    {errorFor('message')}
                </div>

                {/* Hidden from people, filled in only by spam bots. */}
                <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                    <label htmlFor={fieldId('website')}>Website</label>
                    <input
                        id={fieldId('website')}
                        name='website'
                        type='text'
                        tabIndex={-1}
                        autoComplete='off'
                        value={fields.website}
                        onChange={update('website')}
                    />
                </div>
            </div>

            {status === 'error' && serverMessage && (
                <p id={`${id}-server`} role='alert' className='mt-6 border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    {serverMessage}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                    By sending this form you agree to our{' '}
                    <Link href='/privacy' className='underline underline-offset-2 hover:text-foreground'>privacy policy</Link>.
                </p>

                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='inline-flex items-center justify-center gap-3 bg-primary px-8 py-4 text-primary-foreground transition-colors hover:bg-ocean focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-70'
                >
                    {status === 'sending'
                        ? (
                            <>
                                <span className='eyebrow'>Sending</span>
                                <Loader2 className='size-4 animate-spin' aria-hidden='true' />
                            </>
                        )
                        : (
                            <>
                                {section?.button_label
                                    ? <span className='eyebrow' data-mdlfy-cms={cms('page_sections', section.id, 'button_label')}>{section.button_label}</span>
                                    : <span className='eyebrow'>Send inquiry</span>}
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </>
                        )}
                </button>
            </div>
        </form>
    )
}

export default ContactForm
